import { DialogWithHero } from "@jugl-web/ui-components/web/DialogWithHero";
import { useTranslations } from "@jugl-web/utils";
import React, { useMemo, useState } from "react";
import { Button } from "@jugl-web/ui-kit";
import { FaqEntry } from "@jugl-web/ui-components";
import { useNavigation } from "@web-src/modules/navigation/hooks/useNavigation";
import { ReactComponent as MembersIcon } from "./assets/members.svg";
import { ReactComponent as ArrowIcon } from "./assets/arrow.svg";

export const OutOfSeatsDialog: React.FC<{
  isOpen: boolean;
  usedSeats: number;
  totalSeats: number;
  extraSeats?: number;
  isFreePlan?: boolean;
  onRequestClose: () => void;
}> = ({
  isOpen,
  usedSeats,
  totalSeats,
  extraSeats = 0,
  isFreePlan = false,
  onRequestClose,
}) => {
  const { t } = useTranslations();
  const { navigateToPage } = useNavigation();
  const [openId, setOpenId] = useState<string | null>(null);
  const planSeats = Math.max(0, totalSeats - extraSeats);
  const isOutOfSeats = usedSeats >= totalSeats;

  const questions = useMemo(
    () => [
      {
        id: "how-seats-work",
        title: t({
          id: "cpanel-page.how-seats-are-working",
          defaultMessage: "How Seats are working?",
        }),
        content: t({
          id: "cpanel-page.seats-limit-explainer",
          defaultMessage:
            "Both active and pending users count toward your workspace's total seat limit. Once you reach the maximum number of seats allowed by your current plan, you will need to upgrade your subscription to invite additional users.",
        }),
      },
      {
        id: "autopay-support",
        title: t({
          id: "cpanel-page.does-jugl-support-autopay",
          defaultMessage: "Does Jugl support an Autopay?",
        }),
        content: t({
          id: "cpanel-page.autopay-support-answer",
          defaultMessage:
            "Yes. For your convenience, subscriptions renew automatically. Your payment method on file will be charged on a monthly or annual basis, depending on the billing cycle you select at checkout.",
        }),
      },
      {
        id: "modify-subscription-plan",
        title: t({
          id: "cpanel-page.modify-subscription-plan",
          defaultMessage: "Can I modify my Jugl subscription plan?",
        }),
        content: t({
          id: "cpanel-page.modify-subscription-plan-answer",
          defaultMessage:
            "Absolutely. You can upgrade, downgrade, or cancel your Jugl subscription at any time. Any applicable prorated charges or refunds will be automatically calculated based on your current plan and the exact date you make the change.",
        }),
      },
    ],
    [t]
  );

  return (
    <DialogWithHero
      isOpen={isOpen}
      onClose={onRequestClose}
      className="w-[780px]"
      upperTitle={
        <span className="text-grey-200 font-secondary text-[22px] font-light leading-[33px]">
          {isOutOfSeats
            ? t({
                id: "cpanel-page.out-of-seats",
                defaultMessage: "Out of Seats 🚨",
              })
            : t({
                id: "cpanel-page.team-members",
                defaultMessage: "Team Members",
              })}
        </span>
      }
      title={
        <>
          <MembersIcon />
          <span className="font-secondary text-[40px] font-semibold leading-[60px] text-white">
            {t(
              {
                id: "cpanel-page.seats-used-amount",
                defaultMessage:
                  "{used} of {total} {total, plural, one {Seat} other {Seats}} used",
              },
              { used: usedSeats, total: totalSeats }
            )}
          </span>
        </>
      }
      description={
        <>
          <span>
            {t(
              {
                id: "cpanel-page.plan-seat-count",
                defaultMessage:
                  "{count} Plan {count, plural, one {Seat} other {Seats}}",
              },
              { count: planSeats }
            )}
          </span>
          {!isFreePlan && (
            <>
              <span> • </span>
              <span>
                {t(
                  {
                    id: "cpanel-page.extra-seat-count",
                    defaultMessage:
                      "{count} Extra {count, plural, one {Seat} other {Seats}}",
                  },
                  { count: extraSeats }
                )}
              </span>
            </>
          )}
        </>
      }
      headerChildren={
        <Button
          className="mt-[34px] h-12 w-[300px]"
          iconEnd={<ArrowIcon />}
          onClick={() => navigateToPage("cxWorkspacePlans")}
        >
          {isFreePlan
            ? t({
                id: "cpanel-page.manage-plan",
                defaultMessage: "Manage Plan",
              })
            : t({
                id: "cpanel-page.get-extra-seats",
                defaultMessage: "Get Extra Seats",
              })}
        </Button>
      }
    >
      <div className="flex flex-col items-center justify-center gap-5 py-7 px-[26px]">
        <div className="font-secondary text-xl font-semibold leading-[30px] text-black">
          {t({
            id: "cpanel-page.got-questions",
            defaultMessage: "Got questions? We have answers! 🙌",
          })}
        </div>
        <div className="flex w-full flex-col gap-2">
          {questions.map((question) => (
            <FaqEntry
              key={question.id}
              question={question.title}
              answer={question.content}
              isOpen={openId === question.id}
              onOpenSwitch={() =>
                setOpenId((prev) => (prev === question.id ? null : question.id))
              }
            />
          ))}
        </div>
      </div>
    </DialogWithHero>
  );
};
